import { useCallback, useEffect, useMemo } from 'react';
import { JustifiModules, useJustifiLoaderShallowStore } from '../stores/use-justifi-loader-store';

export const useLoadJustifi = <T extends JustifiModules>(modulesToLoad: T[], enabled = true) => {
  const { loadModules, getModuleStatus, components } = useJustifiLoaderShallowStore(
    'loadModules',
    'getModuleStatus',
    'components'
  );

  const memoizedModules = useMemo(() => modulesToLoad, [modulesToLoad.join(',')]);

  const moduleStatuses = useMemo(() => {
    return memoizedModules.reduce((acc, moduleName) => {
      return { ...acc, [moduleName]: getModuleStatus(moduleName) };
    }, {} as Record<T, ReturnType<typeof getModuleStatus>>);
  }, [memoizedModules, components]);

  const allModulesLoaded = useMemo(() => {
    return memoizedModules.every((moduleName) => getModuleStatus(moduleName).isLoaded);
  }, [memoizedModules, components]);

  useEffect(() => {
    if (enabled) {
      loadModules(memoizedModules).catch((error) => {
        console.error('Failed to load Justifi modules:', error);
      });
    }
  }, [memoizedModules, loadModules, enabled]);

  const forceLoadModule = useCallback(
    (moduleName: T) => {
      return loadModules([moduleName], true).catch((error) => {
        console.error(`Failed to force load Justifi module ${moduleName}:`, error);
      });
    },
    [loadModules]
  );

  return {
    forceLoadModule,
    moduleStatuses,
    allModulesLoaded,
  };
};
